import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Download, Share2, Loader2, AlertCircle, RotateCcw } from "lucide-react";
import { ShareLinkModal } from "@/components/shared/ShareLinkModal";
import { SingleDatePicker } from "@/components/shared/SingleDatePicker";
import { TabHeader } from "@/components/shared/TabHeader";
import dayjs from "dayjs";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { SearchTermSetCard } from "./SearchTermSetCard";
import { GenerateSearchTermsModal } from "./GenerateSearchTermsModal";
import { toast } from "@/hooks/use-toast";
import { useGetSearchTermSets, useRetrySearchTermSet } from "@/resources/search-term/search-term.api";
import { useGetHeroProductDetail } from "@/resources/product/product.api";
import { keywordBankRunApi } from "@/resources/keyword-bank-run";
import { isKeywordBankImporting } from "@/utils/keywordBankImporting";
import { useAppData } from "@/contexts/AppDataContext";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import type { SearchTermSet } from "@/types/search-term";
import { SearchTermStatus } from "@/types/search-term";
import { STALE_THRESHOLD_MS } from "./staleThreshold";

const isStaleSet = (set: SearchTermSet) =>
  Date.now() - new Date(set.created_at).getTime() > STALE_THRESHOLD_MS;

interface SearchTermsContentV2Props {
  productId?: string;
  isPublic?: boolean;
  publicSet?: SearchTermSet;
}

export function SearchTermsContentV2({ productId = "", isPublic = false, publicSet }: SearchTermsContentV2Props) {
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const { selectedBrandId } = useAppData();
  const readOnly = useViewerReadOnly();

  const { data: product } = useGetHeroProductDetail(
    isPublic ? undefined : productId,
    isPublic ? undefined : (selectedBrandId ?? undefined),
    { includeKeywords: false },
  );

  const { data: searchTermSets = [], isLoading } = useGetSearchTermSets(
    isPublic ? undefined : (productId ? Number(productId) : undefined),
  );

  // AUT-196 follow-up: the share view (`isPublic`) is not a product-detail
  // surface and never shows the importing state. `enabled: !isPublic` stops the
  // request from firing at all -- in public mode there is no product id, so an
  // unconditional call hit `GET /products/hero/0/keyword-bank-run`, an
  // `auth:api` route whose 401 bounced every anonymous share visitor to /login.
  // Matches how the other hooks above disable in public mode.
  const { data: keywordBankRunResponse } = keywordBankRunApi.useGetKeywordBankRun(
    Number(productId),
    { enabled: !isPublic },
  );
  const isImporting = !isPublic && isKeywordBankImporting(keywordBankRunResponse?.run);

  const completedSets = useMemo(
    () =>
      isPublic
        ? publicSet
          ? [publicSet]
          : []
        : searchTermSets.filter(
            (s) => s.status === SearchTermStatus.Completed || s.status === SearchTermStatus.Refreshing,
          ),
    [isPublic, publicSet, searchTermSets],
  );

  const formatDate = (raw: string) => {
    const d = dayjs(raw);
    return d.isValid() ? d.format("MMM D, YYYY") : raw;
  };

  const versionOptions = useMemo(
    () =>
      completedSets.map((set) => ({
        id: String(set.id),
        label: `${formatDate(set.created_at)} · ${set.type === "grouped" ? "Grouped" : "Single"}`,
      })),
    [completedSets]
  );

  const [selectedVersionId, setSelectedVersionId] = useState<string>("");
  const [prevOptionCount, setPrevOptionCount] = useState(0);

  useEffect(() => {
    if (versionOptions.length === 0) return;

    const latestId = versionOptions[0].id;
    const isNewSet = versionOptions.length > prevOptionCount;
    const selectionMissing = !versionOptions.some((v) => v.id === selectedVersionId);

    if (selectionMissing || isNewSet) {
      setSelectedVersionId(latestId);
    }

    setPrevOptionCount(versionOptions.length);
  }, [versionOptions]);

  const selectedSet = useMemo(
    () =>
      completedSets.find((s) => String(s.id) === selectedVersionId) ??
      completedSets[0] ??
      null,
    [completedSets, selectedVersionId]
  );

  const pendingSets = searchTermSets.filter(
    (s) => s.status === SearchTermStatus.Pending || s.status === SearchTermStatus.Generating,
  );

  const failedSets = searchTermSets.filter((s) => s.status === "failed");

  const retryMutation = useRetrySearchTermSet();
  const handleRetry = (set: SearchTermSet) => {
    retryMutation.mutate({ id: set.id, productId: Number(productId), type: set.type });
  };

  const handleExport = () => {
    if (!selectedSet) return;

    const rows = selectedSet.entries.map((e) => e.content);
    const csvContent = rows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const identifier = product?.asin ?? productId;
    const dateStr = dayjs().format("YYYY-MM-DD");
    link.download = `search-terms-${identifier}-${dateStr}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // One condition for "nothing to show", shared by the header (no kebab on the bare empty state)
  // and the body (the empty-state block), so the two cannot drift.
  const isEmpty = !isLoading && completedSets.length === 0 && pendingSets.length === 0 && failedSets.length === 0;

  return (
    <div className="space-y-4">
      <TabHeader
        title="Search Terms"
        description="Backend search terms for this product."
        menuItems={
          !isPublic && !(isEmpty && !isImporting) ? (
            <>
              <DropdownMenuItem onClick={handleExport} className="gap-2" disabled={!selectedSet}>
                <Download className="h-4 w-4" />Export
              </DropdownMenuItem>
              {!readOnly ? (
                <DropdownMenuItem onClick={() => setShareModalOpen(true)} className="gap-2" disabled={!selectedSet}>
                  <Share2 className="h-4 w-4" />Share
                </DropdownMenuItem>
              ) : null}
            </>
          ) : undefined
        }
      >
        {!isPublic && (
          <>
            {versionOptions.length > 1 && (
              <SingleDatePicker
                options={versionOptions}
                value={selectedVersionId}
                onValueChange={setSelectedVersionId}
                placeholder="Select version"
                className="min-w-[200px] max-w-full"
              />
            )}
            {!readOnly ? (
              <Button
                disabled={isLoading || isImporting}
                onClick={() => setGenerateModalOpen(true)}
              >
                <Plus className="h-4 w-4" />
                Generate
              </Button>
            ) : null}
          </>
        )}
      </TabHeader>

      {!isPublic && isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {!isPublic && pendingSets.map((set) => (
            <div
              key={set.id}
              className="rounded-lg border border-border p-4 flex flex-col items-center justify-center gap-2 text-muted-foreground"
            >
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Generating search terms...</span>
              </div>
              {isStaleSet(set) && (
                <div className="flex items-center gap-1 text-amber-600 dark:text-amber-500 text-xs">
                  <AlertCircle className="h-3.5 w-3.5" />
                  <span>This is taking longer than expected. You may want to try again.</span>
                </div>
              )}
            </div>
          ))}

          {!isPublic && failedSets.map((set) => (
            <div
              key={set.id}
              className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span className="text-sm">
                  Search term generation failed{set.status_info ? `: ${set.status_info}` : "."}
                </span>
              </div>
              {!readOnly && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={retryMutation.isPending}
                  onClick={() => handleRetry(set)}
                >
                  {retryMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4" />
                  )}
                  Retry
                </Button>
              )}
            </div>
          ))}

          {isEmpty ? (
            isImporting ? (
              // AUT-196 follow-up: keywords are still importing, so generation
              // is not yet meaningful -- keep the same empty-state shell but
              // add the "please wait" line and disable the action.
              <div className="text-center py-12 text-muted-foreground border border-border rounded-sm bg-card">
                <p>No search term sets generated yet.</p>
                <p className="text-sm mt-1">
                  Please wait for the product's keywords to fully load before generating search terms.
                </p>
                {!readOnly && (
                  <Button
                    variant="outline-on-white"
                    className="mt-3"
                    disabled
                    onClick={() => setGenerateModalOpen(true)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Generate Search Terms
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground border border-border rounded-sm bg-card">
                <p>No search term sets generated yet.</p>
                <p className="text-sm mt-1">
                  Generate backend search terms from this product's keyword bank.
                </p>
                {!isPublic && !readOnly && (
                  <Button
                    variant="outline-on-white"
                    className="mt-3"
                    onClick={() => setGenerateModalOpen(true)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Generate Search Terms
                  </Button>
                )}
              </div>
            )
          ) : selectedSet ? (
            <SearchTermSetCard
              key={selectedSet.id}
              termSet={selectedSet}
              productId={Number(productId)}
              productName={product?.name ?? ""}
              brandId={selectedBrandId}
              isLatest={selectedSet.id === completedSets[0]?.id}
              isPublic={isPublic}
              // FR-2: only the per-product screen offers selection, and only for a
              // non-viewer -- the share view (`isPublic`) never does.
              selectionEnabled={!isPublic && !readOnly}
            />
          ) : null}
        </>
      )}

      {!isPublic && !readOnly && (
        <GenerateSearchTermsModal
          open={generateModalOpen}
          onOpenChange={setGenerateModalOpen}
          productId={Number(productId)}
          productName={product?.name ?? "Product"}
        />
      )}

      {!isPublic && !readOnly && (
        <ShareLinkModal
          open={shareModalOpen}
          onOpenChange={setShareModalOpen}
          shareableType="search_term_set"
          shareableId={selectedSet ? String(selectedSet.id) : undefined}
          resourceName={selectedSet ? `Search Terms · ${product?.asin ?? productId}` : "Search Terms"}
        />
      )}
    </div>
  );
}
